import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Palette, Upload, Save, Eye, Globe, Building2, ImagePlus, Loader2, X, Check, Plus, Star, Trash2, FileCode, Type, Pencil, MoreVertical } from 'lucide-react';
import { getBranding, saveBranding, uploadLogo, getBrandThemes, createBrandTheme, updateBrandTheme, activateBrandTheme } from '../lib/api';
import api from '../lib/api';
import { useBrand } from '../lib/BrandContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

export default function BrandingPage() {
  const { refreshBrand } = useBrand();
  const [branding, setBranding] = useState({
    company_name: 'CipherCampaign',
    tagline: '',
    logo_url: '',
    primary_color: '#0891b2',
    secondary_color: '#1a1a2e',
    accent_color: '#00b4d8',
    text_color: '#333333',
    background_color: '#f4f4f4',
    font_family: 'Arial, Helvetica, sans-serif',
    footer_text: '',
    contact_email: '',
    phone: '',
    website: '',
    address: '',
    social_links: {},
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [themes, setThemes] = useState([]);
  const [creatingTheme, setCreatingTheme] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [editingTheme, setEditingTheme] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', primary_color: '', secondary_color: '', accent_color: '', font_heading: '' });
  const [editSaving, setEditSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadBranding(); loadThemes(); }, []);

  const loadThemes = async () => {
    try {
      const res = await getBrandThemes();
      setThemes(res.data || []);
    } catch (err) { console.error(err); }
  };

  const handleCreateTheme = async () => {
    if (!newThemeName.trim()) return;
    setCreatingTheme(true);
    try {
      const themeData = {
        name: newThemeName,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        accent_color: branding.accent_color,
        text_color: branding.text_color,
        background_color: branding.background_color,
        font_heading: 'Space Grotesk',
        font_body: branding.font_family || 'Inter',
        logo_url: branding.logo_url,
        company_address: '',
        legal_text: '',
        email_header_html: '',
        email_footer_html: '',
        is_active: false,
      };
      await createBrandTheme(themeData);
      toast.success('Theme created!');
      setNewThemeName('');
      loadThemes();
    } catch (err) {
      toast.error('Failed to create theme');
    } finally {
      setCreatingTheme(false);
    }
  };

  const handleActivateTheme = async (themeId) => {
    try {
      await activateBrandTheme(themeId);
      toast.success('Theme activated! Brand applied globally.');
      loadThemes();
      refreshBrand();
    } catch (err) {
      toast.error('Failed to activate theme');
    }
  };

  const handleUpdateThemeFooter = async (themeId, field, value) => {
    try {
      await updateBrandTheme(themeId, { [field]: value });
      loadThemes();
      toast.success(`${field.replace(/_/g, ' ')} updated`);
    } catch (err) {
      toast.error('Failed to update theme');
    }
  };

  const handleDeleteTheme = async (themeId) => {
    try {
      await api.delete(`/api/branding/themes/${themeId}`);
      toast.success('Theme deleted');
      loadThemes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete theme');
    }
  };

  const openEditTheme = (theme) => {
    setEditForm({
      name: theme.name || '',
      primary_color: theme.primary_color || '#0891b2',
      secondary_color: theme.secondary_color || '#1a1a2e',
      accent_color: theme.accent_color || '#00b4d8',
      font_heading: theme.font_heading || 'Space Grotesk',
    });
    setEditingTheme(theme);
  };

  const handleSaveEditTheme = async () => {
    if (!editingTheme) return;
    setEditSaving(true);
    try {
      await updateBrandTheme(editingTheme.id, editForm);
      toast.success('Theme updated');
      setEditingTheme(null);
      loadThemes();
      refreshBrand();
    } catch (err) {
      toast.error('Failed to update theme');
    } finally {
      setEditSaving(false);
    }
  };

  const loadBranding = async () => {
    try {
      const res = await getBranding();
      if (res.data) {
        setBranding(prev => ({ ...prev, ...res.data }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveBranding(branding);
      toast.success('Branding saved! New campaigns will use these settings.');
      refreshBrand();
    } catch (err) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPEG, GIF, SVG, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const res = await uploadLogo(file);
      if (res.data?.logo_url) {
        setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
        toast.success('Logo uploaded successfully!');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to upload logo';
      toast.error(errMsg);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setBranding(prev => ({ ...prev, logo_url: '' }));
  };

  const generatePreview = () => {
    const html = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:${branding.background_color};font-family:${branding.font_family};">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
<tr><td align="center" style="padding:20px;">
<table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);">
  <!-- Header -->
  <tr>
    <td style="background:linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color});padding:32px 40px;text-align:center;">
      ${branding.logo_url ? `<img src="${branding.logo_url}" alt="${branding.company_name}" style="max-height:50px;margin-bottom:12px;" />` : ''}
      <h1 style="color:#ffffff;margin:0;font-size:24px;font-family:${branding.font_family};">${branding.company_name}</h1>
      ${branding.tagline ? `<p style="color:rgba(255,255,255,0.8);margin:8px 0 0;font-size:14px;">${branding.tagline}</p>` : ''}
    </td>
  </tr>
  <!-- Body -->
  <tr>
    <td style="padding:32px 40px;color:${branding.text_color};">
      <h2 style="margin:0 0 16px;font-size:20px;color:${branding.secondary_color};">Email Preview</h2>
      <p style="margin:0 0 16px;font-size:14px;line-height:1.6;">This is a preview of how your branded emails will look. The colors, logo, and typography you configured will be applied to all generated email campaigns.</p>
      <table role="presentation" cellspacing="0" cellpadding="0" border="0">
        <tr><td style="border-radius:6px;background:${branding.accent_color};text-align:center;">
          <a style="display:inline-block;padding:12px 32px;color:#ffffff;text-decoration:none;font-weight:bold;font-size:14px;">Call to Action</a>
        </td></tr>
      </table>
    </td>
  </tr>
  <!-- Footer -->
  <tr>
    <td style="background:${branding.secondary_color};padding:24px 40px;text-align:center;">
      <p style="color:rgba(255,255,255,0.7);margin:0;font-size:12px;">${branding.footer_text || `\u00A9 ${new Date().getFullYear()} ${branding.company_name}. All rights reserved.`}</p>
    </td>
  </tr>
</table>
</td></tr></table>
</body></html>`;
    setPreviewHtml(html);
  };

  useEffect(() => {
    generatePreview();
  }, [branding]);

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="branding-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <Palette className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Corporate Identity</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Branding</h1>
              <p className="text-sm text-white/50 mt-0.5">Themes apply universally across all surfaces</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] btn-press" data-testid="save-branding-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Branding'}
          </Button>
        </div>
      </div>

      {/* Theme Manager */}
      <Card className="bg-[hsl(var(--pane))] border border-white/10 mb-6 animate-fade-in-up" style={{boxShadow: 'var(--shadow-elev-1)', animationDelay: '0.05s'}}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[hsl(var(--accent))]" />
              <CardTitle className="text-sm font-medium">Brand Themes</CardTitle>
              <Badge variant="outline" className="text-[10px]">{themes.length} theme{themes.length !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Input value={newThemeName} onChange={(e) => setNewThemeName(e.target.value)} placeholder="New theme name..." className="h-8 text-xs w-48 bg-secondary/40" data-testid="new-theme-name" onKeyDown={(e) => e.key === 'Enter' && handleCreateTheme()} />
              <Button size="sm" onClick={handleCreateTheme} disabled={!newThemeName.trim() || creatingTheme} className="bg-[hsl(var(--accent))] text-white text-xs h-8" data-testid="create-theme-btn">
                <Plus className="w-3 h-3 mr-1" /> Create
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {themes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No themes yet. Create one from your current branding settings.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {themes.map(theme => (
                <div key={theme.id} className={`p-3 rounded-lg border transition-all ${theme.is_active ? 'ring-2 ring-[hsl(var(--accent))]/40 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5' : 'border-border hover:border-border/80 bg-secondary/20'}`} data-testid={`theme-card-${theme.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold truncate">{theme.name}</span>
                      {theme.is_active && (
                        <Badge className="text-[9px] bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/25 shrink-0">Active</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!theme.is_active && (
                        <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => handleActivateTheme(theme.id)} data-testid={`activate-theme-${theme.id}`}>
                          Activate
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`theme-menu-${theme.id}`}>
                            <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-32">
                          <DropdownMenuItem onClick={() => openEditTheme(theme)} data-testid={`edit-theme-${theme.id}`}>
                            <Pencil className="w-3 h-3 mr-2" /> Edit
                          </DropdownMenuItem>
                          {!theme.is_active && (
                            <DropdownMenuItem onClick={() => handleDeleteTheme(theme.id)} className="text-destructive focus:text-destructive" data-testid={`delete-theme-${theme.id}`}>
                              <Trash2 className="w-3 h-3 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[theme.primary_color, theme.secondary_color, theme.accent_color].filter(Boolean).map((c, i) => (
                      <div key={i} className="w-5 h-5 rounded border border-white/10" style={{ backgroundColor: c }} />
                    ))}
                    <span className="text-[10px] text-muted-foreground ml-1 font-mono">{theme.font_heading || 'System'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="identity" className="space-y-4">
        <TabsList>
          <TabsTrigger value="identity">Identity</TabsTrigger>
          <TabsTrigger value="email-templates">Email Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="space-y-4">
          {/* Company Info */}
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Company Info</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Company Name</Label>
                <Input value={branding.company_name} onChange={(e) => setBranding({...branding, company_name: e.target.value})}
                  placeholder="Your Company" className="bg-secondary/40" data-testid="branding-company-name" />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input value={branding.tagline} onChange={(e) => setBranding({...branding, tagline: e.target.value})}
                  placeholder="Your tagline" className="bg-secondary/40" />
              </div>
              <div>
                <Label>Footer Text</Label>
                <Input value={branding.footer_text} onChange={(e) => setBranding({...branding, footer_text: e.target.value})}
                  placeholder="&copy; 2026 Company. All rights reserved." className="bg-secondary/40" />
              </div>
              <Separator className="my-2" />
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Contact Info (used in email footers)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Contact Email</Label>
                  <Input value={branding.contact_email || ''} onChange={(e) => setBranding({...branding, contact_email: e.target.value})}
                    placeholder="alerts@company.com" className="bg-secondary/40 text-xs" data-testid="branding-contact-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={branding.phone || ''} onChange={(e) => setBranding({...branding, phone: e.target.value})}
                    placeholder="+1 (555) 123-4567" className="bg-secondary/40 text-xs" data-testid="branding-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Website</Label>
                  <Input value={branding.website || ''} onChange={(e) => setBranding({...branding, website: e.target.value})}
                    placeholder="https://www.company.com" className="bg-secondary/40 text-xs" data-testid="branding-website" />
                </div>
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input value={branding.address || ''} onChange={(e) => setBranding({...branding, address: e.target.value})}
                    placeholder="123 Business Ave, City, ST" className="bg-secondary/40 text-xs" data-testid="branding-address" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <ImagePlus className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Company Logo</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Logo Preview */}
              {branding.logo_url ? (
                <div className="relative group p-4 bg-secondary/30 rounded-lg flex items-center justify-center border border-border" data-testid="logo-preview-container">
                  <img
                    src={branding.logo_url}
                    alt="Company logo"
                    className="max-h-16 max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid="logo-preview-image"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-muted-foreground hover:text-destructive"
                    onClick={handleRemoveLogo}
                    data-testid="remove-logo-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 text-xs text-[hsl(var(--status-success))]">
                    <Check className="w-3 h-3" /> Uploaded
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-secondary/20 rounded-lg border border-dashed border-border flex flex-col items-center justify-center gap-2 text-center" data-testid="logo-upload-dropzone">
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No logo uploaded yet</p>
                  <p className="text-xs text-muted-foreground">PNG, JPEG, SVG, or WebP (max 5MB)</p>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                  data-testid="logo-file-input"
                />
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload Logo</>
                  )}
                </Button>
              </div>

              <Separator />

              {/* Manual URL */}
              <div>
                <Label className="text-xs text-muted-foreground">Or enter a logo URL manually</Label>
                <Input
                  value={branding.logo_url}
                  onChange={(e) => setBranding({...branding, logo_url: e.target.value})}
                  placeholder="https://example.com/logo.png"
                  className="bg-secondary/40 font-mono text-xs mt-1"
                  data-testid="branding-logo-url"
                />
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Color Theme</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: 'primary_color', label: 'Primary Color', desc: 'Header, main branding' },
                { key: 'secondary_color', label: 'Secondary Color', desc: 'Footer, accents' },
                { key: 'accent_color', label: 'Accent Color', desc: 'CTA buttons, links' },
                { key: 'text_color', label: 'Text Color', desc: 'Body text' },
                { key: 'background_color', label: 'Background Color', desc: 'Email background' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center gap-3">
                  <input type="color" value={branding[key] || '#000000'}
                    onChange={(e) => setBranding({...branding, [key]: e.target.value})}
                    className="w-10 h-9 rounded border border-border cursor-pointer flex-shrink-0" />
                  <div className="flex-1">
                    <Label className="text-sm">{label}</Label>
                    <p className="text-[10px] text-muted-foreground">{desc}</p>
                  </div>
                  <Input value={branding[key] || ''} onChange={(e) => setBranding({...branding, [key]: e.target.value})}
                    className="w-28 bg-secondary/40 font-mono text-xs" data-testid={`branding-${key}`} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Typography */}
          <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Typography</CardTitle>
            </CardHeader>
            <CardContent>
              <Label>Font Family</Label>
              <Input value={branding.font_family} onChange={(e) => setBranding({...branding, font_family: e.target.value})}
                placeholder="Arial, Helvetica, sans-serif" className="bg-secondary/40 font-mono text-sm" />
            </CardContent>
          </Card>
        </div>

        {/* Live Preview */}
        <div>
          <Card className="bg-card/90 border border-border sticky top-6" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium text-muted-foreground">Live Preview</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border border-border rounded-lg overflow-hidden bg-white" style={{minHeight: '450px'}}>
                <iframe srcDoc={previewHtml} className="w-full" style={{minHeight: '450px', border: 'none'}}
                  title="Branding Preview" sandbox="allow-same-origin" data-testid="branding-preview" />
              </div>
              {/* Color Swatches */}
              <div className="flex items-center gap-2 mt-4">
                <span className="text-xs text-muted-foreground">Palette:</span>
                {['primary_color', 'secondary_color', 'accent_color'].map(key => (
                  <div key={key} className="w-8 h-8 rounded-lg border border-border" style={{backgroundColor: branding[key]}} title={key} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </TabsContent>

      {/* Email Templates Tab */}
      <TabsContent value="email-templates">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Email Header Template */}
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileCode className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-medium">Email Header Template</CardTitle>
                </div>
                <p className="text-xs text-muted-foreground">Custom HTML header for all outgoing emails. Use {'{{brand.company_name}}'}, {'{{brand.logo_url}}'}, {'{{brand.primary_color}}'}, etc.</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={branding.email_header_html || ''}
                  onChange={(e) => setBranding({...branding, email_header_html: e.target.value})}
                  placeholder="<table>...custom header HTML...</table>"
                  className="bg-secondary/40 font-mono text-xs min-h-[120px]"
                  data-testid="email-header-template"
                />
              </CardContent>
            </Card>

            {/* Email Footer Template */}
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileCode className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-medium">Email Footer Template</CardTitle>
                </div>
                <p className="text-xs text-muted-foreground">Must include: logo, company name, address, unsubscribe link, legal text.</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={branding.email_footer_html || ''}
                  onChange={(e) => setBranding({...branding, email_footer_html: e.target.value})}
                  placeholder="<table>...branded footer with {{brand.company_name}}, {{brand.address}}, unsubscribe...</table>"
                  className="bg-secondary/40 font-mono text-xs min-h-[120px]"
                  data-testid="email-footer-template"
                />
              </CardContent>
            </Card>

            {/* Footer Text */}
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Footer Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Footer Text</Label>
                  <Input value={branding.footer_text || ''} onChange={(e) => setBranding({...branding, footer_text: e.target.value})} placeholder="&copy; 2026 Company. All rights reserved." className="bg-secondary/40 text-xs" data-testid="footer-text" />
                </div>
                <div>
                  <Label className="text-xs">Company Address</Label>
                  <Input value={branding.company_address || ''} onChange={(e) => setBranding({...branding, company_address: e.target.value})} placeholder="123 Business Ave, City, State 12345" className="bg-secondary/40 text-xs" data-testid="company-address" />
                </div>
                <div>
                  <Label className="text-xs">Legal Text</Label>
                  <Textarea value={branding.legal_text || ''} onChange={(e) => setBranding({...branding, legal_text: e.target.value})} placeholder="This email was sent because..." className="bg-secondary/40 text-xs min-h-[60px]" data-testid="legal-text" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Email Preview with Branding */}
          <div>
            <Card className="bg-card/90 border border-border sticky top-6" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-medium text-muted-foreground">Branded Email Preview</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border border-border rounded-lg overflow-hidden bg-white" style={{minHeight: '450px'}}>
                  <iframe srcDoc={previewHtml} className="w-full" style={{minHeight: '450px', border: 'none'}}
                    title="Branded Email Preview" sandbox="allow-same-origin" data-testid="email-template-preview" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TabsContent>
      </Tabs>

      {/* Edit Theme Dialog */}
      <Dialog open={!!editingTheme} onOpenChange={(open) => !open && setEditingTheme(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit Theme</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Theme Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-secondary/40" data-testid="edit-theme-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Primary</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={editForm.primary_color} onChange={(e) => setEditForm({...editForm, primary_color: e.target.value})} className="w-8 h-8 rounded border-0 cursor-pointer" data-testid="edit-theme-primary" />
                  <Input value={editForm.primary_color} onChange={(e) => setEditForm({...editForm, primary_color: e.target.value})} className="bg-secondary/40 text-xs h-8 font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Secondary</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={editForm.secondary_color} onChange={(e) => setEditForm({...editForm, secondary_color: e.target.value})} className="w-8 h-8 rounded border-0 cursor-pointer" data-testid="edit-theme-secondary" />
                  <Input value={editForm.secondary_color} onChange={(e) => setEditForm({...editForm, secondary_color: e.target.value})} className="bg-secondary/40 text-xs h-8 font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Accent</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={editForm.accent_color} onChange={(e) => setEditForm({...editForm, accent_color: e.target.value})} className="w-8 h-8 rounded border-0 cursor-pointer" data-testid="edit-theme-accent" />
                  <Input value={editForm.accent_color} onChange={(e) => setEditForm({...editForm, accent_color: e.target.value})} className="bg-secondary/40 text-xs h-8 font-mono" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Heading Font</Label>
              <Input value={editForm.font_heading} onChange={(e) => setEditForm({...editForm, font_heading: e.target.value})} placeholder="Space Grotesk" className="bg-secondary/40 text-xs" data-testid="edit-theme-font" />
            </div>
            {/* Color preview */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/30">
              <span className="text-xs text-muted-foreground mr-2">Preview:</span>
              {[editForm.primary_color, editForm.secondary_color, editForm.accent_color].map((c, i) => (
                <div key={i} className="w-8 h-8 rounded border border-white/10" style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditingTheme(null)} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSaveEditTheme} disabled={editSaving} className="bg-[hsl(var(--accent))] text-white text-xs" data-testid="save-edit-theme-btn">
              {editSaving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
